import React, { createContext, useCallback, useContext, useState, useEffect, ReactNode } from 'react';
import WorkspaceService from '../services/workspace.service';

const SELECTED_WORKSPACE_ID_KEY = 'selected_workspace_id'

export interface Workspace {
    id: number
    name: string
    is_public: boolean
    members_count?: number
}

interface WorkspaceContextType {
    isWorkspaceLoading: boolean
    workspace: Workspace | null
    workspaces: Workspace[]
    fetchWorkspaces: () => Promise<Workspace[]>
    // Refetches the list and returns it without changing the active workspace.
    // Used when a workspace is missing from the cached list, e.g. right after
    // the user was invited to it.
    reloadWorkspaces: () => Promise<Workspace[]>
    setWorkspace: (workspace: Workspace) => void
    setWorkspaces: React.Dispatch<React.SetStateAction<Workspace[]>>
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

interface WorkspaceProviderProps {
    children: ReactNode
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
    const [isWorkspaceLoading, setIsWorkspaceLoading] = useState<boolean>(true)
    const [workspace, setWorkspace] = useState<Workspace | null>(null)
    const [workspaces, setWorkspaces] = useState<Workspace[]>([])
    const [isReady, setIsReady] = useState<boolean>(false)

    const handleSetWorkspace = useCallback((nextWorkspace: Workspace) => {
        setWorkspace(nextWorkspace)
        localStorage.setItem(SELECTED_WORKSPACE_ID_KEY, String(nextWorkspace.id))
    }, [])

    const fetchWorkspaces = async (): Promise<Workspace[]> => {
        try {
            const data = await WorkspaceService.getWorkspaces()
            const allWorkspaces: Workspace[] = data.workspaces || data || []
            const savedWorkspaceId = localStorage.getItem(SELECTED_WORKSPACE_ID_KEY)
            const restoredWorkspace = allWorkspaces.find((item: Workspace) => String(item.id) === savedWorkspaceId)

            setWorkspaces(allWorkspaces)

            if (allWorkspaces.length > 0) {
                const fallbackWorkspace = restoredWorkspace || allWorkspaces[0]

                setWorkspace((prev) => {
                    // Keep the workspace selected by the current navigation, but
                    // replace its stale object with the freshly fetched data.
                    const refreshedCurrentWorkspace = prev
                        ? allWorkspaces.find((item) => String(item.id) === String(prev.id))
                        : null
                    const nextWorkspace = refreshedCurrentWorkspace || fallbackWorkspace

                    localStorage.setItem(SELECTED_WORKSPACE_ID_KEY, String(nextWorkspace.id))

                    return nextWorkspace
                })
            } else {
                setWorkspace(null)
                localStorage.removeItem(SELECTED_WORKSPACE_ID_KEY)
            }

            return allWorkspaces
        } catch (e) {
            console.error('Failed to fetch workspaces:', e)

            return []
        }
        finally {
            setIsReady(true)
        }
    }

    const reloadWorkspaces = useCallback(async (): Promise<Workspace[]> => {
        const data = await WorkspaceService.getWorkspaces()
        const allWorkspaces: Workspace[] = data.workspaces || data || []
        let currentWorkspaces = allWorkspaces

        // Keep the previous array when the membership is unchanged: callers run
        // this from effects that depend on `workspaces`, and handing back a new
        // reference every time would re-trigger them forever.
        setWorkspaces((previousWorkspaces) => {
            const isSameList = previousWorkspaces.length === allWorkspaces.length
                && previousWorkspaces.every((workspace, index) => workspace.id === allWorkspaces[index].id)

            currentWorkspaces = isSameList ? previousWorkspaces : allWorkspaces

            return currentWorkspaces
        })

        return currentWorkspaces
    }, [])

    const firstLoad = async () => {
        await fetchWorkspaces()
        setIsWorkspaceLoading(false)
    }


    useEffect(() => {
        firstLoad()
    }, [])

    if(!isReady){
        return null
    }

    return (
        <WorkspaceContext.Provider value={{ isWorkspaceLoading, workspace, workspaces, setWorkspace: handleSetWorkspace, setWorkspaces, fetchWorkspaces, reloadWorkspaces }}>
            {children}
        </WorkspaceContext.Provider>
    )
}

export const useWorkspace = () => {
    const context = useContext(WorkspaceContext);
    if (context === undefined) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
}
